import React from "react";
import { X, Copy, Check } from "lucide-react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { CLIENT, CLIENT_VENDOR } from "../../constants/roles";

const ArticleDetailsModal = ({ isOpen, onClose, title, abstract }) => {
  const [copiedField, setCopiedField] = React.useState("");

  const user = useSelector((state) => state?.auth?.user);

  if (!isOpen) return null;

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopiedField(field);
      toast.success(`${field} copied successfully.`);

      setTimeout(() => {
        setCopiedField("");
      }, 2000);
    } catch (error) {
      toast.error(`Failed to copy ${field.toLowerCase()}.`);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="article-details-title"
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-[#d3cccc] bg-white px-5 py-4">
          <h2
            id="article-details-title"
            className="text-lg font-semibold text-gray-800"
          >
            Article Details
          </h2>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close modal"
            className="rounded-md p-2 text-gray-500 hover:bg-gray-100"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 p-5">
          {/* Title */}
          <section>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="font-semibold text-gray-800">Title</h3>

              {user?.role !== CLIENT_VENDOR && user?.role !== CLIENT && (
              <button
                type="button"
                onClick={() => handleCopy(title, "Title")}
                disabled={!title}
                className="inline-flex items-center gap-2 rounded-md border border-[#d3cccc] px-3 py-1.5 text-sm text-[#521785] hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {copiedField === "Title" ? (
                  <Check size={16} />
                ) : (
                  <Copy size={16} />
                )}
                {copiedField === "Title" ? "Copied" : "Copy Title"}
              </button>
              )}
            </div>

            <div className="whitespace-pre-wrap break-words rounded-md border border-[#d3cccc] bg-gray-50 p-4 text-sm leading-6 text-gray-700">
              {title || "No title available."}
            </div>
          </section>

          {/* Abstract */}
          <section>
            <div className="mb-2 flex items-center justify-between gap-3">
              <h3 className="font-semibold text-gray-800">Abstract</h3>

              {user?.role !== CLIENT_VENDOR && user?.role !== CLIENT && (
              <button
                type="button"
                onClick={() => handleCopy(abstract, "Abstract")}
                disabled={!abstract}
                className="inline-flex items-center gap-2 rounded-md border border-[#d3cccc] px-3 py-1.5 text-sm text-[#521785] hover:bg-purple-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {copiedField === "Abstract" ? (
                  <Check size={16} />
                ) : (
                  <Copy size={16} />
                )}
                {copiedField === "Abstract" ? "Copied" : "Copy Abstract"}
              </button>
              )}
            </div>

            <div className="max-h-72 overflow-y-auto whitespace-pre-wrap break-words rounded-md border border-[#d3cccc] bg-gray-50 p-4 text-sm leading-6 text-gray-700">
              {abstract || "No abstract available."}
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="flex justify-end border-t border-[#d3cccc] px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-[#d3cccc] px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ArticleDetailsModal;